/**
 * Analytics for the Weave Metric Alert feature.
 *
 * Two surfaces:
 * - `WeaveAlertAnalytics.sourceSelected` fires on the source toggle, capturing
 *   source interest even when no alert is ultimately created.
 * - `getWeaveAlertTrackProps` supplies weave-specific properties for the generic
 *   'Automation Creation' event so creations are attributable by source.
 */
import {Analytics} from '../../../../../services/analytics';
import {EventTriggeringConditionType} from '../../TriggerEventTypes';
import {SPAN_DURATION_PATH, SPAN_TOTAL_TOKENS_PATH} from './constants';
import {useWeaveMetricThresholdStore} from './store';
import {type SourceType, type WeaveMetricThresholdConfig} from './types';

type SpanMetricKind = 'total_tokens' | 'duration_ms' | 'custom';
type SpanFilterKey = 'operationName' | 'spanName' | 'agentName';

export type WeaveAlertTrackProps = {
  weaveSourceType?: SourceType;
  weaveSpanMetricKind?: SpanMetricKind;
  weaveSpanFiltersUsed?: SpanFilterKey[];
};

export const WeaveAlertAnalytics = {
  /** User toggles the alert source (Operation / Monitor / Agent spans). */
  sourceSelected(data: {sourceType: SourceType}) {
    Analytics.track('Weave Alert Source Selected', data);
  },
};

/**
 * Weave properties for the shared 'Automation Creation' event. Returns {} for
 * non-weave event types so the generic drawer stays decoupled from the store.
 */
export const getWeaveAlertTrackProps = (
  eventType: EventTriggeringConditionType | undefined
): WeaveAlertTrackProps => {
  if (eventType !== EventTriggeringConditionType.WeaveMetricThreshold) {
    return {};
  }
  const {metricFilter} = useWeaveMetricThresholdStore.getState();
  const props: WeaveAlertTrackProps = {
    weaveSourceType: metricFilter.sourceType,
  };
  if (metricFilter.sourceType === 'SPANS') {
    props.weaveSpanMetricKind = spanMetricKind(metricFilter.metricPath);
    props.weaveSpanFiltersUsed = spanFiltersUsed(metricFilter);
  }
  return props;
};

const spanMetricKind = (metricPath: string | undefined): SpanMetricKind => {
  if (metricPath === SPAN_TOTAL_TOKENS_PATH) {
    return 'total_tokens';
  }
  if (metricPath === SPAN_DURATION_PATH) {
    return 'duration_ms';
  }
  return 'custom';
};

const spanFiltersUsed = (
  metricFilter: WeaveMetricThresholdConfig['metricFilter']
): SpanFilterKey[] => {
  const used: SpanFilterKey[] = [];
  if (metricFilter.spanOperationName) {
    used.push('operationName');
  }
  if (metricFilter.spanName) {
    used.push('spanName');
  }
  if (metricFilter.spanAgentName) {
    used.push('agentName');
  }
  return used;
};
